import { useEffect } from "react";

/**
 * Per-page SEO + AI-search optimization:
 *  - Sets <title> and meta description
 *  - Updates Open Graph + Twitter Card tags
 *  - Sets/updates rel=canonical
 *  - Injects (and replaces) page-specific JSON-LD blocks via [data-seo="page"]
 *
 * Designed for AI overviews (ChatGPT, Gemini, Claude, Perplexity) and
 * Google's AI search to extract clean entity data per route.
 */
export default function usePageMeta({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value, createAttrs) => {
      if (!value) return;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        Object.entries(createAttrs).forEach(([k, v]) => tag.setAttribute(k, v));
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description, {
        name: "description",
      });
    }

    // Open Graph
    if (title) {
      setMeta('meta[property="og:title"]', "content", title, {
        property: "og:title",
      });
      setMeta('meta[name="twitter:title"]', "content", title, {
        name: "twitter:title",
      });
    }
    if (description) {
      setMeta('meta[property="og:description"]', "content", description, {
        property: "og:description",
      });
      setMeta('meta[name="twitter:description"]', "content", description, {
        name: "twitter:description",
      });
    }
    setMeta('meta[property="og:type"]', "content", ogType, {
      property: "og:type",
    });
    setMeta(
      'meta[property="og:site_name"]',
      "content",
      "Time Furniture",
      { property: "og:site_name" }
    );
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image", {
      name: "twitter:card",
    });

    // Canonical URL
    const canonicalUrl =
      canonical ||
      (typeof window !== "undefined"
        ? `https://timefurniture.ae${window.location.pathname}`
        : null);
    if (canonicalUrl) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
      setMeta('meta[property="og:url"]', "content", canonicalUrl, {
        property: "og:url",
      });
    }

    // og:image (absolute URL preferred)
    if (ogImage) {
      const absImg =
        ogImage.startsWith("http")
          ? ogImage
          : typeof window !== "undefined"
          ? `${window.location.origin}${ogImage}`
          : ogImage;
      setMeta('meta[property="og:image"]', "content", absImg, {
        property: "og:image",
      });
      setMeta('meta[name="twitter:image"]', "content", absImg, {
        name: "twitter:image",
      });
      setMeta(
        'meta[property="og:image:alt"]',
        "content",
        title || "Time Furniture",
        { property: "og:image:alt" }
      );
    }

    // Page-scoped JSON-LD (replace ALL previously injected page scripts)
    document.head.querySelectorAll('script[data-seo="page"]').forEach((el) => el.remove());
    if (jsonLd) {
      const scripts = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scripts.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo", "page");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, canonical, ogImage, ogType, jsonLd]);
}
